<app-page-heading [heading]="heading"
                  [hasBackButton]="true"
                  [backPageName]="backPageName"
                  [tabName]="'claims'">
</app-page-heading>

<!---------------------------->
<!-- Section Banner-->
<!---------------------------->
@if (!isAllowedToSubmitTransaction() || isImportedTransaction) {
  <app-banner [bannerText]="bannerText"></app-banner>
}


<div class="stepper-container">
  <mat-horizontal-stepper #stepper [linear]="true">

    <!---------------------------->
    <!-- STEP: Transactions:-->
    <!---------------------------->

    <mat-step #claimStep [completed]="isClaimFormValid()">
      <ng-template matStepLabel>Transaction</ng-template>
      <form [formGroup]="claimFormGroup">
        @if (hasOpenAuthorisations()) {
          <div class="open-authorisation-message open-authorisation-warning" role="status">
            <mat-icon>info</mat-icon>
            <span>Existing authorisations found. Review before continuing.</span>
            <button type="button" mat-button color="primary" (click)="viewAuthorisations()">View Auths</button>
          </div>
        } @else if (openAuthorisationLookupFailed()) {
          <div class="open-authorisation-message open-authorisation-failure" role="status">
            <mat-icon>info</mat-icon>
            <span>Unable to check for existing authorisations. You can continue.</span>
          </div>
        }

        <mat-grid-list cols="3" rowHeight="90px">

          <!-- Row-1 -->
          <mat-grid-tile class="selection_align">
            <mat-form-field>
              <mat-label>Processing Class</mat-label>
              <mat-select [formControl]="processingClassFormControl" required
                          (selectionChange)="onProcessingClassSelectionChanged($event.source.value)">
                @for (processingClass of processingClasses; track processingClass.name) {
                  <mat-option [value]="processingClass.name">
                    {{processingClass.name}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-icon class="info-icon" [matTooltip]="getInfoTooltip('Processing Class')">help_outline</mat-icon>
          </mat-grid-tile>

          <mat-grid-tile class="selection_align">
            <mat-form-field>
              <mat-label>{{dynamicDateTitle}}</mat-label>
              <input [formControl]="claimReceiveDateFormControl" [matDatepicker]="claimReceiveDatePicker" matInput
                     required>
              <mat-datepicker-toggle [for]="claimReceiveDatePicker" matIconSuffix>
              </mat-datepicker-toggle>
              <mat-datepicker #claimReceiveDatePicker></mat-datepicker>
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Claim Receive Date')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          @if (showConsentFields()) {
            <mat-grid-tile class="m-l-45">
              <div class="col-2"
                   [ngClass]="{
                    'col-3': consentIndicatorFormControl.value,
                    'col-12': !consentIndicatorFormControl.value
                    }">
                <div class="m-l-5">
                  <mat-slide-toggle [formControl]="consentIndicatorFormControl">
                    Consent?
                  </mat-slide-toggle>
                </div>
              </div>
              @if (consentIndicatorFormControl.value) {
                <div class="col-9 flex items-center">
                  <mat-form-field class="w-75">
                    <mat-label>Consent Reason</mat-label>
                    <mat-select [formControl]="consentReasonFormControl" required>
                      @for (consentReason of consentReasons; track consentReason.name) {
                        <mat-option [value]="consentReason.name">
                          {{consentReason.name}}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-icon class="consent-icon info-icon" [matTooltip]="getInfoTooltip('Consent Reason')">help_outline</mat-icon>
                </div>
              }
            </mat-grid-tile>
          } @else {
            <!--Spacer-->
            <mat-grid-tile></mat-grid-tile>
          }

        </mat-grid-list>

        <!-------------------------------------------------->
        <!--Section Visible: Normal Users-->
        <!-------------------------------------------------->
        @if (requiresCaseToCaptureTransaction) {
          <mat-grid-list  cols="3" rowHeight="90px">
            <!-- Row-2 -->
            <mat-grid-tile class="selection_align">
              <mat-form-field>
                <mat-label>{{memberNameLabel}}</mat-label>
                <input required matInput type="text" [placeholder]="getInfoTooltip('Member Last Name')"
                       [formControl]="insuredPersonNameFormControl" (blur)="blurInsuredPersonName()"
                       (input)="onInputChangedSearchInsuredPersons($event)" [matAutocomplete]="insuredPersonAuto">
                <mat-autocomplete #insuredPersonAuto="matAutocomplete" [panelWidth]="'40vw'">
                  @for (insuredPerson of filteredInsuredPersons; track getInsuredPersonDisplayString(insuredPerson)) {
                    <mat-option (onSelectionChange)="onInsuredPersonOptionChanged($event.source.value)"
                                [value]="getInsuredPersonDisplayString(insuredPerson)"
                                class="custom-mat-option">
                      {{getInsuredPersonDisplayString(insuredPerson)}}
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (insuredPersonNameFormControl.value) {
                  <button (click)="clearInsuredPersonName()"  aria-label="Clear" mat-icon-button matSuffix>
                    <mat-icon style="color: grey;">clear</mat-icon>
                  </button>
                }
              </mat-form-field>
              <mat-icon class="info-icon" [matTooltip]="getInfoTooltip('Member Last Name')">help_outline</mat-icon>
            </mat-grid-tile>

            <mat-grid-tile>

              @if (generalCases?.length) {
                <mat-form-field>
                  <mat-label>{{selectedProcessingClass}} Case</mat-label>
                  <mat-select [formControl]="generalCaseFormControl"
                              (selectionChange)="onGeneralCaseSelectionChanged($event.source.value)"
                              required>
                    @for (case of generalCases; track getCaseDisplayString(case)) {
                      <mat-option [value]="getCaseDisplayString(case)">
                        {{getCaseDisplayString(case)}}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <mat-icon class="info-icon" [matTooltip]="getInfoTooltip('Case')">
                  help_outline
                </mat-icon>
              } @else {

                <!--Medical Cases When General Cases === 0 -->
                @if (isMedicalProcessingClass()) {
                  <label class="column-label claim-column-label">
                    {{this.selectedGeneralCase?.caseDTO?.name}}
                  </label>
                } @else {
                  <div class="center">
                    <label class="column-label claim-column-label">No {{selectedProcessingClass}} cases associated with
                      the {{memberNameLabel}}</label>
                  </div>
                }
              }

            </mat-grid-tile>

            <!--Spacer-->
            <mat-grid-tile></mat-grid-tile>

          </mat-grid-list>
        }

        <!-------------------------------------------------->
        <!--Section Visible for Both:
          - Normal Users with Customization:
          - Provider Portal Users with defaults:
        -->
        <!-------------------------------------------------->

        <app-member-policy-product-link #memberPolicyProductLink
                                        [isAuthorisation]="isAuthorisation()"
                                        [isProviderPortal]=isProviderPortal
                                        [isEditMode]="isEditMode"
                                        [selectedInsuredPerson]="selectedInsuredPerson"
                                        [selectedInsuredPersonWithDates]="selectedInsuredPersonWithDates"
                                        [selectedPolicy]="selectedPolicy"
                                        [selectedProduct]="selectedProduct"
                                        [enableSearchInputs]="!requiresCaseToCaptureTransaction"
                                        (selectedInsuredPersonNotify)="onSelectedInsuredPersonNotifiedFromPP($event)"
                                        (isValid)="isMemberPolicyProductFormValid = $event">

        @if (requiresCaseToCaptureTransaction) {
              <ng-template #memberNameTpl>
                @if (selectedInsuredPerson) {
                  <app-content-field [label]="memberNameLabel + ' Name'"
                                     [isEditable]="allowOverride()"
                                     [content]="getInsuredPersonNameDisplayString(selectedInsuredPerson)"
                                     (editClick)="overridePersonNameClicked($event)"></app-content-field>
                }
              </ng-template>

              <ng-template #memberAssociationTpl>
                <!-- Content Field -->
                @if (selectedInsuredPerson && !editAssociation) {
                  <app-content-field
                    [label]="memberNameLabel + ' Association'"
                    [isEditable]="allowOverride()"
                    [content]="createAssociationDisplayString(selectedInsuredPerson)"
                    (editClick)="overrideAssociationClicked($event)"></app-content-field>
                }

                <!-- Input Field -->
                @if (selectedInsuredPerson && editAssociation) {
                  <mat-form-field>
                    <mat-label>Association</mat-label>
                    <mat-select [formControl]="associationOverrideFormControl" required>
                      @for (association of associations; track association.name) {
                        <mat-option [value]="association.name">
                          {{association.name}}
                        </mat-option>
                      }
                    </mat-select>
                    <button (click)="showAssociationContentField()" aria-label="Clear"
                            mat-icon-button matSuffix style="margin-left: 16px; margin-right: -12px">
                      <mat-icon matTooltip="Clear input" style="color: grey;">clear</mat-icon>
                    </button>
                    <button (click)="saveAssociationOverride()" aria-label="save"
                            mat-icon-button matSuffix>
                      <mat-icon class="table-icon" matTooltip="Save input">save</mat-icon>
                    </button>
                    <mat-hint>Ensure the override is saved or cancelled using the icons</mat-hint>
                  </mat-form-field>
                }
              </ng-template>

              <ng-template #dateOfBirthTpl>
                <!-- Content Field -->
                @if (selectedInsuredPerson && !editDateOfBirth) {
                  <app-content-field
                                     [label]="'Date of Birth'"
                                     [isEditable]="allowOverride()"
                                     [content]="selectedInsuredPerson.dateOfBirth | date: dateFormat"
                                     (editClick)="overrideDateOfBirthClicked($event)"></app-content-field>
                }

                <!-- Input Field -->
                @if (selectedInsuredPerson && editDateOfBirth) {
                  <mat-form-field>
                    <mat-label>Date of Birth</mat-label>
                    <input [formControl]="dateOfBirthOverrideFormControl" [matDatepicker]="dateOfBirthPicker" matInput
                           required>
                    <mat-datepicker-toggle [for]="dateOfBirthPicker" matIconSuffix>
                    </mat-datepicker-toggle>
                    <mat-datepicker #dateOfBirthPicker></mat-datepicker>
                    <button (click)="showDateOfBirthContentField()" aria-label="Clear"
                            mat-icon-button matSuffix style="margin-left: 16px; margin-right: -12px">
                      <mat-icon matTooltip="Clear input" style="color: grey;">clear</mat-icon>
                    </button>
                    <button (click)="saveDateOfBirthOverride()" aria-label="save"
                            mat-icon-button matSuffix>
                      <mat-icon class="table-icon" matTooltip="Save input">save</mat-icon>
                    </button>
                    <mat-hint>Ensure the override is saved or cancelled using the icons</mat-hint>
                  </mat-form-field>
                }
              </ng-template>

              <ng-template #genderTpl>
                <!-- Content Field -->
                @if (selectedInsuredPerson && !editGender) {
                  <app-content-field
                                     [label]="'Gender'"
                                     [isEditable]="allowOverride()"
                                     [content]="selectedInsuredPerson.gender.name"
                                     (editClick)="overrideGenderClicked($event)"></app-content-field>
                }

                <!-- Input Field -->
                @if (selectedInsuredPerson && editGender) {
                  <mat-form-field>
                    <mat-label>Gender</mat-label>
                    <mat-select [formControl]="genderOverrideFormControl" required>
                      @for (gender of genders; track gender.name) {
                        <mat-option [value]="gender.name">
                          {{gender.name}}
                        </mat-option>
                      }
                    </mat-select>
                    <button (click)="showGenderContentField()" aria-label="Clear"
                            mat-icon-button matSuffix style="margin-left: 16px; margin-right: -12px">
                      <mat-icon matTooltip="Clear input" style="color: grey;">clear</mat-icon>
                    </button>
                    <button (click)="saveGenderOverride()" aria-label="save"
                            mat-icon-button matSuffix>
                      <mat-icon class="table-icon" matTooltip="Save input">save</mat-icon>
                    </button>
                    <mat-hint>Ensure the override is saved or cancelled using the icons</mat-hint>
                  </mat-form-field>
                }
              </ng-template>

              <ng-template #nationalIDTpl>
                <!-- Content Field -->
                @if (selectedInsuredPerson && !editIdNumber) {
                  <app-content-field [label]="'National ID Number'"
                                     [content]="selectedInsuredPerson.nationalIdNumber | slice:0:13"
                                     [isEditable]="allowOverride()"
                                     (editClick)="overrideIdNumberClicked($event)"></app-content-field>
                }

                <!-- Input Field -->
                @if (selectedInsuredPerson && editIdNumber) {
                  <mat-form-field>
                    <mat-label>National ID Number</mat-label>
                    <input [formControl]="idNumberOverrideFormControl" matInput required>
                    <button (click)="showIdNumberContentField()" aria-label="Clear"
                            mat-icon-button matSuffix style="margin-left: 16px; margin-right: -12px">
                      <mat-icon style="color: grey;" matTooltip="Clear input">clear</mat-icon>
                    </button>
                    <button (click)="saveIdNumberOverride()" aria-label="save"
                            mat-icon-button matSuffix>
                      <mat-icon class="table-icon" matTooltip="Save input">save</mat-icon>
                    </button>
                    <mat-hint>Ensure the override is saved or cancelled using the icons</mat-hint>
                  </mat-form-field>
                }
              </ng-template>
            }
        </app-member-policy-product-link>

        <div class="button-container">
          <button mat-raised-button color="warn"
                  (click)="resetClaimFormGroup()"
                  [disabled]="!isAllowedToSubmitTransaction()">
            Clear
            <ng-container [ngTemplateOutlet]="helpIcon"></ng-container>
          </button>
          <button mat-raised-button matStepperNext color="primary">Next</button>
        </div>
      </form>
    </mat-step>

    <!---------------------------->
    <!-- STEP: Encounter:-->
    <!---------------------------->

    <mat-step #encounterStep [completed]="encounterFormGroup.valid">
      <ng-template matStepLabel>Encounter</ng-template>
      <form [formGroup]="encounterFormGroup">
        <mat-grid-list cols="3" rowHeight="90px">

          <!-- Row-1 -->
          <mat-grid-tile>
            <mat-form-field [ngClass]="{'w-75': showTuneIndicators()}">
              <mat-label>{{providerNameLabel}}</mat-label>
              <input required matInput type="text" #providerTrigger="matAutocompleteTrigger"
                     [placeholder]="getInfoTooltip(providerNameLabel)"
                     [formControl]="providerNameFormControl"
                     (blur)="blurProviderName()"
                     (input)="onInputChangedSearchProviders($event)"
                     (keydown.enter)="onProviderEnter($event, providerTrigger)" [matAutocomplete]="providerAuto">
              <mat-autocomplete #providerAuto="matAutocomplete" [panelWidth]="'40vw'">
                @for (provider of filteredProviders; track getProviderDisplayString(provider)) {
                  <mat-option (onSelectionChange)="onProviderOptionChanged($event.source.value)"
                              [value]="getProviderDisplayString(provider)"
                              class="custom-mat-option">
                    {{getProviderDisplayString(provider)}}
                  </mat-option>
                }
              </mat-autocomplete>
              @if (providerNameFormControl.value && !providerNameFormControl.disabled) {
                <button (click)="clearProviderName()" aria-label="Clear" mat-icon-button matSuffix>
                  <mat-icon style="color: grey;">clear</mat-icon>
                </button>
              }
            </mat-form-field>

            @if (showTuneIndicators()) {
              <mat-icon (click)="onClickShowEncounterOverrideIndicatorsDialog($event)" class="info-icon hand-cursor">
                tune
              </mat-icon>
            }
            <mat-icon (click)="showSelectedProviderDialog(selectedProvider)" class="info-icon hand-cursor">visibility
            </mat-icon>
            <mat-icon [matTooltip]="getInfoTooltip(providerNameLabel)" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          <mat-grid-tile>
            <mat-form-field>
              <mat-label>Pay To</mat-label>
              <mat-select [formControl]="payeeFormControl" required
                          (selectionChange)="onPayeeSelectionChanged($event.source.value)">
                @for (payee of (isFuneral ? funeralPayees : medicalPayees); track payee.name) {
                  <mat-option [value]="payee.name">
                    {{payee.name}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Pay To')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          <mat-grid-tile>
            <mat-form-field>
              <mat-label>Encounter Type</mat-label>
              <mat-select [formControl]="encounterTypeFormControl" required
                          (selectionChange)="onEncounterTypeSelectionChanged($event.source.value)">
                @for (type of encounterTypes; track type.name) {
                  <mat-option [value]="type.name">
                    {{type.name}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Encounter Type')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          <!-- Row-2 -->
          <mat-grid-tile>
            <span class="reference-fields">
              <span class="fields-row">
                <mat-form-field>
                  <mat-label>Reference Number</mat-label>
                  <input matInput type="text"
                         [formControl]="referenceNumberFormControl"
                         (blur)="onBlurReferenceNumber()"
                         [placeholder]="referenceNumberPlaceholderText">
                  @if (referenceNumberFormControl.hasError('jiraNotFound')) {
                    <mat-error>
                      <span>No issue found in Jira with the given key</span>
                    </mat-error>
                  }
                </mat-form-field>

                <mat-icon></mat-icon>

                <mat-form-field>
                  <mat-label>Invoice Number</mat-label>
                  <input matInput type="text"
                         [formControl]="invoiceNumberFormControl">
                </mat-form-field>
              </span>

                @if (jiraEnabled && containsJiraKey && jiraKeyExists) {
                  <div class="jira-hint-row">
                    <a [href]="jiraUrl" target="_blank" matTooltip="Open Jira issue in new tab" class="jira-link">
                      <img ngSrc="../../../../../../assets/jira-service-management-logo.svg"
                           alt="Jira Service Management Logo" class="jira-logo" height="16" width="16">
                      <span class="jira-text">{{ jiraIssue.summary }}</span>
                      <mat-icon>open_in_new</mat-icon>
                    </a>
                  </div>
                }
              </span>
            <mat-icon></mat-icon>
            <mat-icon [matTooltip]="getInfoTooltip('Invoice Number')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          <mat-grid-tile>
            @if (isNominatedBeneficiary) {
              <app-content-field [label]="'Beneficiary National Number'"
                                 [content]="getNominatedBeneficiaryDisplayString()"></app-content-field>

              <mat-icon (click)="refreshBeneficiary()" class="info-icon hand-cursor">
                refresh
              </mat-icon>
            }

            @if (isEstate || isAlternativeBeneficiary) {
              <mat-form-field>
                <mat-label>{{beneficiaryHeading}} Name</mat-label>
                <input [required]="isEstate || isAlternativeBeneficiary" matInput type="text"
                       [placeholder]="getInfoTooltip('Beneficiary Name')"
                       [formControl]="beneficiaryFormControl" (blur)="blurBeneficiaryName()"
                       (input)="onInputChangedSearchBeneficiaries($event)" [matAutocomplete]="beneficiaryAuto">
                <mat-autocomplete #beneficiaryAuto="matAutocomplete" [panelWidth]="'40vw'">
                  @for (beneficiary of filteredBeneficiaries; track getInsuredPersonDisplayString(beneficiary)) {
                    <mat-option (onSelectionChange)="onBeneficiaryOptionChanged($event.source.value)"
                                [value]="getInsuredPersonDisplayString(beneficiary)"
                                class="custom-mat-option">
                      {{getInsuredPersonDisplayString(beneficiary)}}
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (beneficiaryFormControl.value) {
                  <button (click)="clearBeneficiaryName()" aria-label="Clear"
                          mat-icon-button matSuffix>
                    <mat-icon style="color: grey;">clear</mat-icon>
                  </button>
                }
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Beneficiary Name')"
                        class="info-icon">help_outline
              </mat-icon>
            }
          </mat-grid-tile>

          <mat-grid-tile>
            <mat-form-field>
              <mat-label>{{encounterStartDateLabel}}</mat-label>
              <input [formControl]="encounterStartDateFormControl" [matDatepicker]="encounterStartDatePicker" matInput
                     required (dateChange)="encounterStartDateChanged()"
                     max="{{encounterEndDateFormControl.value | date:hyphenatedDateFormat}}">
              <mat-datepicker-toggle [for]="encounterStartDatePicker" matIconSuffix>
              </mat-datepicker-toggle>
              <mat-datepicker #encounterStartDatePicker></mat-datepicker>
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Encounter Start Date')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>

          <!-- Row-3 -->
          @if (isMedicalProcessingClass()) {
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>{{invoiceDateLabel}}</mat-label>
                <input [formControl]="invoiceDateFormControl" [matDatepicker]="invoiceDatePicker" matInput
                       [required]="isMedicalProcessingClass()">
                <mat-datepicker-toggle [for]="invoiceDatePicker" matIconSuffix>
                </mat-datepicker-toggle>
                <mat-datepicker #invoiceDatePicker></mat-datepicker>
              </mat-form-field>
              <!--Empty Spacer Icon-->
              <mat-icon></mat-icon>
              <mat-icon [matTooltip]="getInfoTooltip('Invoice Date')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>
          }
          @if (isFuneral) {
            <mat-grid-tile></mat-grid-tile>
          }

          @if (isEstate || isAlternativeBeneficiary) {
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>Reason for Bypassing Nominated Beneficiary</mat-label>
                <mat-select [formControl]="beneficiaryNarrativeFormControl" required>
                  @for (narrative of beneficiaryNarratives; track narrative.name) {
                    <mat-option [value]="narrative.name">
                      {{narrative.name}}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Reason for Bypassing Nominated Beneficiary')" class="info-icon">
                help_outline
              </mat-icon>
            </mat-grid-tile>
          }
          @if (!(isEstate || isAlternativeBeneficiary)) {
            <mat-grid-tile></mat-grid-tile>
          }

          @if (showEndDateInput()) {
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>End Date</mat-label>
                <input [formControl]="encounterEndDateFormControl" [matDatepicker]="encounterEndDatePicker" matInput
                       [required]="encounterEndDateRequired()"
                       min="{{encounterStartDateFormControl.value | date:hyphenatedDateFormat}}">
                <mat-datepicker-toggle [for]="encounterEndDatePicker" matIconSuffix>
                </mat-datepicker-toggle>
                <mat-datepicker #encounterEndDatePicker></mat-datepicker>
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Encounter End Date')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>
          }
          @if (isFuneral) {
            <mat-grid-tile></mat-grid-tile>
          }

          <!-- Row-4 -->
          @if (isMedicalProcessingClass()) {
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>Referring Provider Name</mat-label>
                <input matInput type="text" #referringProviderTrigger="matAutocompleteTrigger"
                       [placeholder]="getInfoTooltip('Referring Provider Name')"
                       [formControl]="referringProviderNameFormControl" (blur)="onBlurReferringProviderName()"
                       (input)="onInputChangedSearchReferringProviders($event)"
                       (keydown.enter)="onReferringProviderEnter($event, referringProviderTrigger)" [matAutocomplete]="referringProviderAuto">
                <mat-autocomplete #referringProviderAuto="matAutocomplete" [panelWidth]="'40vw'">
                  @for (provider of filteredReferringProviders; track getProviderDisplayString(provider)) {
                    <mat-option (onSelectionChange)="onReferringProviderOptionChanged($event.source.value)"
                                [value]="getProviderDisplayString(provider)"
                                class="custom-mat-option">
                      {{getProviderDisplayString(provider)}}
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (referringProviderNameFormControl.value) {
                  <button (click)="clearReferringProviderName()"
                          aria-label="Clear"
                          mat-icon-button matSuffix>
                    <mat-icon style="color: grey;">clear</mat-icon>
                  </button>
                }
              </mat-form-field>
              <mat-icon (click)="showSelectedProviderDialog(selectedReferringProvider)" class="info-icon hand-cursor">
                visibility
              </mat-icon>
              <mat-icon [matTooltip]="getInfoTooltip('Referring Provider Name')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>
          }

          <!-- Row-5 -->
          @if (isMedicalProcessingClass() && isClaim) {

            <mat-grid-tile></mat-grid-tile>

            <mat-grid-tile>
              <mat-form-field>
                <mat-label>Authorisation Number</mat-label>
                <mat-select [formControl]="authorisationNumberFormControl" [required]="isMedicalProcessingClass() && isClaim"
                            (selectionChange)="onAuthorisationSelectionChanged($event.source.value)">
                  @for (authorisation of authorisations; track getAuthorisationValue(authorisation)) {
                    <mat-option [value]="getAuthorisationValue(authorisation)">
                      {{getAuthorisationDisplayString(authorisation)}}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              @if (isClaim) {
                <mat-icon [matTooltip]="getInfoTooltip('Authorisation Number')" class="info-icon">
                  help_outline
                </mat-icon>
              }
            </mat-grid-tile>
          }

          @if (isFuneral) {
            <mat-grid-tile></mat-grid-tile>
            <mat-grid-tile>
              @if (isNominatedBeneficiary || isEstate || isAlternativeBeneficiary) {
                <button mat-raised-button
                        color="primary"
                        (click)="openAddPersonTab()">
                  <mat-icon>add</mat-icon>
                  Add Person
                </button>
              }
            </mat-grid-tile>
            <mat-grid-tile></mat-grid-tile>
          }


          @if (isMedicalProcessingClass()) {
            @if (isAuthorisation()) {
              <mat-grid-tile></mat-grid-tile>
              <mat-grid-tile></mat-grid-tile>
            }

            <mat-grid-tile>
              <mat-form-field>
                <mat-label>{{isPrimarySubClassPharmacy ? 'Prescribing Provider Name' : 'Treating Provider Name'}}</mat-label>
                <input matInput type="text" #prescribingProviderTrigger="matAutocompleteTrigger"
                       [placeholder]="getInfoTooltip('Prescribing Provider')"
                       [formControl]="prescribingProviderNameFormControl" (blur)="onBlurPrescribingProviderName()"
                       (input)="onInputChangedSearchPrescribingProviders($event)"
                       (keydown.enter)="onPrescribingProviderEnter($event, prescribingProviderTrigger)" [matAutocomplete]="prescribingProviderAuto">
                <mat-autocomplete #prescribingProviderAuto="matAutocomplete" [panelWidth]="'40vw'">
                  @for (prescribingProvider of filteredPrescribingProviders; track getProviderDisplayString(prescribingProvider)) {
                    <mat-option class="custom-mat-option"
                                [value]="getProviderDisplayString(prescribingProvider)"
                                (onSelectionChange)="onPrescribingProviderOptionChanged($event.source.value)">
                      {{getProviderDisplayString(prescribingProvider)}}
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (prescribingProviderNameFormControl.value) {
                  <button (click)="clearPrescribingProvider()"
                          aria-label="Clear" mat-icon-button matSuffix>
                    <mat-icon style="color: grey;">clear</mat-icon>
                  </button>
                }
              </mat-form-field>
              <mat-icon (click)="showSelectedProviderDialog(selectedPrescribingProvider)" class="info-icon hand-cursor">
                visibility
              </mat-icon>
              <mat-icon [matTooltip]="getInfoTooltip('Prescribing Provider')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>
          }

        </mat-grid-list>

        <div class="button-container">
          <button mat-raised-button matStepperPrevious color="primary">Back</button>
          <button mat-raised-button color="warn"
                  (click)="resetEncounterFormGroup()"
                  [disabled]="!isAllowedToSubmitTransaction()">
            Clear
            <ng-container [ngTemplateOutlet]="helpIcon"></ng-container>
          </button>
          <button mat-raised-button color="primary" (click)="encounterNextClick()">Next</button>
        </div>
      </form>
    </mat-step>

    <!---------------------------->
    <!-- STEP: Diagnosis:-->
    <!---------------------------->

    <mat-step #diagnosisStep
              [completed]="diagnosisFormGroup.valid">
      <ng-template matStepLabel>Diagnoses</ng-template>
      <form [formGroup]="diagnosisFormGroup">
        <mat-grid-list cols="2" rowHeight="90px">
          <mat-grid-tile>
            <mat-form-field>
              <mat-label>Diagnosis Description</mat-label>
              <input matInput type="text" [placeholder]="getInfoTooltip('Diagnosis Description')"
                     [formControl]="diagnosisDescriptionFormControl" (blur)="blurDiagnosisDescription()"
                     (input)="onInputChangedSearchDiagnoses($event)" [matAutocomplete]="diagnosisAuto">
              <mat-autocomplete #diagnosisAuto="matAutocomplete" [panelWidth]="'40vw'">
                @for (diagnosis of filteredDiagnoses; track getDiagnosisDisplayString(diagnosis)) {
                  <mat-option (onSelectionChange)="onDiagnosisOptionChanged($event.source.value)"
                              [value]="getDiagnosisDisplayString(diagnosis)"
                              class="custom-mat-option">
                    {{getDiagnosisDisplayString(diagnosis)}}
                  </mat-option>
                }
              </mat-autocomplete>
              @if (diagnosisDescriptionFormControl.value) {
                <button (click)="clearDiagnosisDescription()"
                        aria-label="Clear"
                        mat-icon-button matSuffix>
                  <mat-icon style="color: grey;">clear</mat-icon>
                </button>
              }
            </mat-form-field>

            <mat-form-field class="w-20 m-l-10">
              <mat-label>Qualifier</mat-label>
              <mat-select [disabled]="isPrimarySelectedOnlyOneAllowed()" [formControl]="diagnosisTierFormControl">
                @for (tier of diagnosisTiers; track tier.name) {
                  <mat-option [value]="tier.name">
                    {{tier.name}}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Diagnosis Tier')" class="info-icon">help_outline</mat-icon>
          </mat-grid-tile>
          <mat-grid-tile>
          </mat-grid-tile>
        </mat-grid-list>
        @if (selectedDiagnoses.length > 0) {
          <mat-divider style="margin-bottom: 16px"></mat-divider>
        }
        @if (diagnosisFormGroup.getError('minimumDiagnoses') && diagnosisFormGroup.touched) {
          <mat-error class="left-content claim-column-label">
            Please select at least 1 diagnosis
          </mat-error>
        }
        @if (diagnosisFormGroup.getError('primaryDiagnosisSelected') && selectedDiagnoses.length > 0) {
          <mat-error class="left-content claim-column-label">
            Please select at least 1 valid primary diagnosis
          </mat-error>
        }
        @if (selectedDiagnoses.length > 0) {
          <div class="left-content">
            <label class="column-label claim-column-label">Selected Diagnoses</label>
          </div>
        }
        <div class="left-content">
          @for (diagnosis of getSortedSelectedDiagnoses(); track diagnosis.code) {
            <p>
              <b>{{diagnosis.icdTier}}</b><br>
              <b>{{diagnosis.code}}</b>: {{diagnosis.description}}<br>
              <b>Chapter</b>: {{diagnosis.chapter}}<br>
              <b>Block</b>: {{diagnosis.block}}<br>
              <b>Category</b>: {{diagnosis.category}}<br>
              <mat-icon class="table-icon" matTooltip="REMOVE Diagnosis" (click)="removeDiagnosis(diagnosis)">delete
              </mat-icon>
            </p>
          }
        </div>
        <div class="button-container">
          <button mat-raised-button matStepperPrevious color="primary">Back</button>
          <button mat-raised-button color="warn"
                  (click)="resetDiagnosisFormGroup()"
                  [disabled]="!isAllowedToSubmitTransaction()">
            Clear
            <ng-container [ngTemplateOutlet]="helpIcon"></ng-container>
          </button>
          <button mat-raised-button color="primary" (click)="diagnosisNextClick()">Next</button>
        </div>
      </form>
    </mat-step>

    <!---------------------------->
    <!-- STEP: Activity:-->
    <!---------------------------->

    <mat-step #activityStep [completed]="activityFormGroup.valid">
      <ng-template matStepLabel>Activities</ng-template>

      <!---------------------------->
      <!-- Edit Case-->
      <!---------------------------->
      @if (isActivityAdded()) {
        <div style="margin-bottom: 16px">
          <div class="clearfix">
            <div class="col" [ngClass]="{'col-3': isReview,'col-4': !isReview}">
              <ng-container *ngTemplateOutlet="activityTotalTpl"></ng-container>
            </div>
            <div class="col"
                 [ngClass]="{'col-9': isReview,'col-4': !isReview}">
              <div [ngClass]="{
               'flex items-center': isReview,
               'flex justify-center': !isReview
             }">
                <!-- Split template rendering based on isReview -->
                @if (isReview) {
                  <ng-container *ngTemplateOutlet="activityButtons"></ng-container>
                } @else {
                  <ng-container *ngTemplateOutlet="addNoteAndActivityTpl"></ng-container>
                }
              </div>
            </div>
            <!-- Empty space when not in review mode -->
            @if (!isReview) {
              <div class="col col-4"></div>
            }
          </div>
        </div>
      }
      <!---------------------------->
      <!-- Add Case :-->
      <!---------------------------->
      @if (!isActivityAdded()) {
        <div style="margin-bottom: 16px">
          <div class="clearfix">
            <div class="col col-3">
              <ng-container *ngTemplateOutlet="activityTotalTpl"></ng-container>
            </div>
            <div class="col col-7">
              <div class="flex justify-center">
                <ng-container *ngTemplateOutlet="activityButtons"></ng-container>
              </div>
            </div>
          </div>
        </div>
      }

      <!---------------------------->
      <!-- Activity Total Tpl:-->
      <!---------------------------->
      <ng-template #activityTotalTpl>
        <p class="m-l-15 m-b-0 bold">Totals</p>
        <ul>
          <li>Activities: {{activitiesCount}}</li>
          <li>Amount:     {{activitiesAmount | number: '.2'}}</li>
          @if (isMedicalProcessingClass()) {
            <li>No. of Consultations: {{noOfConsultationVisits}}</li>
          }
        </ul>
      </ng-template>

      <!---------------------------->
      <!-- Add Note & Activity Tpl:-->
      <!---------------------------->
      <ng-template #addNoteAndActivityTpl>
        <button #addDocumentButton mat-raised-button color="primary" class="activity-review-button"
                (click)="onClickOpenAddDocumentDialog($event)">
          <mat-icon>attach_file</mat-icon>
          Documents
        </button>
        <button #addNoteButton mat-raised-button color="primary" class="activity-review-button"
                (click)="onClickOpenAddNoteDialog($event)">
          <mat-icon>note_add</mat-icon>
          Notes
        </button>
        <button #addActivityButton mat-raised-button color="primary" class="activity-review-button"
                (click)="addActivityButtonClicked()">
          <mat-icon>add</mat-icon>
          Add Activity
        </button>

      </ng-template>

      <!---------------------------->
      <!-- Activity Buttons:-->
      <!---------------------------->
      <ng-template #activityButtons>
        <div class="global-container">
          @if (isReview) {
            <button #acceptAllButton mat-raised-button color="accent" class="activity-review-button"
                    [disabled]="disableActivityStatusUpdateByRole() || !isAllowedToSubmitTransaction()"
                    (click)="acceptAllActivities()">
              <mat-icon>check_circle</mat-icon>
              Accept All
              @if (disableActivityStatusUpdateByRole()) {
                <mat-icon class="iconAlignment"
                          [matTooltip]="activityStatusTooltip()">help_outline
                </mat-icon>
              }
            </button>
            <button #rejectAllButton mat-raised-button color="warn" class="activity-review-button"
                    [disabled]="disableActivityStatusUpdateByRole() || !isAllowedToSubmitTransaction()"
                    (click)="rejectAllActivities()">
              <mat-icon>cancel</mat-icon>
              Reject All
              @if (disableActivityStatusUpdateByRole()) {
                <mat-icon class="iconAlignment"
                          [matTooltip]="activityStatusTooltip()">help_outline
                </mat-icon>
              }
            </button>
            <button #skipPaymentButton mat-raised-button color="accent" class="activity-review-button"
                    [disabled]="disableMarkClaimAsPaidButton() || !isAllowedToSubmitTransaction()"
                    (click)="markClaimAsPaid()">
              <mat-icon>payment</mat-icon>
              Mark as Paid
              @if (disableMarkClaimAsPaidButton()) {
                <mat-icon class="iconAlignment tooltip-button-disabled-wrapper" [matTooltip]="skipPaymentTooltip()">help_outline</mat-icon>
              }
            </button>
          }

          <ng-container *ngTemplateOutlet="addNoteAndActivityTpl;"></ng-container>
        </div>
      </ng-template>

      <mat-divider class="m-b-10 m-t-10"></mat-divider>

      <!---------------------------->
      <!-- Activity Details:-->
      <!---------------------------->

      @for (subActivityGroup of activityFormArray.controls.slice().reverse(); track subActivityGroup; let i = $index) {
        <div class="form-array-container">
          <div class="center" [hidden]="!subActivityGroup.get(ActivityControls.IsCollapsed).value">
            <label class="column-label claim-column-label">Activity {{activityFormArray.controls.length - i}}</label>
          </div>
          <span class="m-l-15">
            <mat-slide-toggle matInput
                              [formControl]="subActivityGroup.get(ActivityControls.IsCollapsed)">
              Expand Details
            </mat-slide-toggle>
          </span>

          <!-- Claim review grid list -->
          @if (subActivityGroup.get(ActivityControls.IsReview).value) {
            <mat-grid-list cols="3" rowHeight="90px">

              <!---------------------------->
              <!-- Adjudication Status:-->
              <!---------------------------->

              <mat-grid-tile>
                @if (isReview && subActivityGroup.get(ActivityControls.AdjudicationStatus) != null) {
                  <mat-form-field color="accent"
                                  [matTooltip]="activityStatusTooltip(subActivityGroup.get(ActivityControls.AdjudicationStatus).value)">
                    <mat-label>Adjudication Status</mat-label>
                    <mat-select [formControl]="subActivityGroup.get(ActivityControls.AdjudicationStatus)" required
                                [disabled]="isAdjudicationStatusDisabled(subActivityGroup)"
                                (selectionChange)="onAdjudicationStatusSelectionChanged($event.source.value, subActivityGroup.get(ActivityControls.ActivityId).value)">
                      @for (status of adjudicationStatuses; track status.name) {
                        <mat-option [value]="status.name">
                          {{status.name}}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                }
                @if (isReview) {
                  <mat-icon [matTooltip]="getInfoTooltip('Adjudication Status')" class="info-icon">
                    help_outline
                  </mat-icon>
                }
              </mat-grid-tile>

              <mat-grid-tile>
                @if (isReview && subActivityGroup.get(ActivityControls.Justifications) != null) {
                  <app-content-field [label]="' Override Justifications'"
                                     [content]="subActivityGroup.get(ActivityControls.Justifications).value"
                                     [isAccent]="true"></app-content-field>
                }
                @if (subActivityGroup.get(ActivityControls.Justifications).value !== 'No justification set' && !disableActivityStatusUpdateByRole(subActivityGroup.get(ActivityControls.AdjudicationStatus).value)) {
                  <mat-icon
                    matTooltip="EDIT Justification"
                    color="accent"
                    (click)="openJustificationDialog(subActivityGroup.get(ActivityControls.ActivityId).value, subActivityGroup.get(ActivityControls.AdjudicationStatus).value, true)">
                    edit
                  </mat-icon>
                }
                @if (isReview && disableActivityStatusUpdateByRole(subActivityGroup.get(ActivityControls.AdjudicationStatus).value)) {
                  <mat-icon
                    [matTooltip]="activityStatusTooltip(subActivityGroup.get(ActivityControls.AdjudicationStatus).value)" class="info-icon">
                    help_outline
                  </mat-icon>
                }
                @if (subActivityGroup.get(ActivityControls.Justifications).value !== 'No justification set') {
                  <mat-icon
                            matTooltip="VIEW Justifications"
                            color="accent"
                            (click)="openJustificationHistoryDialog(subActivityGroup.get(ActivityControls.ActivityId).value)">visibility
                  </mat-icon>
                }
              </mat-grid-tile>
              <mat-grid-tile>
                @if (isReview && subActivityGroup.get(ActivityControls.RuleMessages) != null) {
                  <app-content-field [label]="'Reason Narratives'"
                                     [content]="subActivityGroup.get(ActivityControls.RuleMessages).value"
                                     [isAccent]="true"></app-content-field>
                }

                <mat-icon matTooltip="VIEW Rules Messages"
                          color="accent"
                          (click)="openRuleMessagesDialog(subActivityGroup.get(ActivityControls.ActivityId).value)">visibility
                </mat-icon>
              </mat-grid-tile>
            </mat-grid-list>
          }

        <form [hidden]="!subActivityGroup.get(ActivityControls.IsCollapsed).value" [formGroup]="asFormGroup(subActivityGroup)">
          <!-- Activity grid list -->
          <mat-grid-list cols="3" rowHeight="90px">
            <!-- Row-1 -->
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>Tariff Code Type</mat-label>
                <mat-select required [formControl]="subActivityGroup.get(ActivityControls.CodingSchemaType)"
                            (selectionChange)="onTariffCodeTypeSelectionChanged(subActivityGroup)">
                  @for (type of (isFuneral ? codingSchemaTypesFuneral : codingSchemaTypes); track type.name) {
                    <mat-option [value]="type.name">
                      {{type.name}}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Coding Schema Type')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>

            <!-------------------------------------->
            <!--.Activity Start/End (Start) Section-->
            <!-------------------------------------->
            <mat-grid-tile>
              <!--Activity Start Date-->
              <mat-form-field subscriptSizing="dynamic"
                [ngClass]="{'w-35': showEndDateInput()}">
                <mat-label>{{ activityStartDateLabel }}</mat-label>
                <input [formControl]="subActivityGroup.get(ActivityControls.ActivityStartDate)"
                       [matDatepicker]="activityStartDatePicker" matInput required
                       min="{{encounterStartDateFormControl?.value | date:hyphenatedDateFormat}}"
                       max="{{subActivityGroup.get(ActivityControls.ActivityEndDate)?.value | date:hyphenatedDateFormat || encounterEndDateFormControl?.value | date:hyphenatedDateFormat}}">
                <mat-datepicker-toggle [for]="activityStartDatePicker" matIconSuffix>
                </mat-datepicker-toggle>
                <mat-datepicker #activityStartDatePicker></mat-datepicker>
                @for (error of getErrorMessages(subActivityGroup); track error) {
                  <mat-error class="font-extra-small error-message-spacing">{{ error }}</mat-error>
                }
              </mat-form-field>
              @if (isMedicalProcessingClass()) {
                <mat-icon
                  [matTooltip]="getInfoTooltip('Activity Start Date')" class="info-icon">help_outline
                </mat-icon>
              }
              @if (isFuneralProcessingClass()) {
                <mat-icon
                  [matTooltip]="getInfoTooltip('Date of Service')" class="info-icon">help_outline
                </mat-icon>
              }

              <!--Activity End Date-->
              @if (showEndDateInput()) {
                <mat-form-field class="w-35 m-l-15" subscriptSizing="dynamic">
                  <mat-label>End Date</mat-label>
                  <input [formControl]="subActivityGroup.get(ActivityControls.ActivityEndDate)"
                         [matDatepicker]="activityEndDatePicker" matInput
                         required
                         min="{{subActivityGroup.get(ActivityControls.ActivityStartDate)?.value || encounterStartDateFormControl?.value | date:hyphenatedDateFormat}}"
                         max="{{encounterEndDateFormControl?.value | date:hyphenatedDateFormat}}">
                  <mat-datepicker-toggle [for]="activityEndDatePicker" matIconSuffix>
                  </mat-datepicker-toggle>
                  <mat-datepicker #activityEndDatePicker></mat-datepicker>
                  @for (error of getErrorMessages(subActivityGroup); track error) {
                    <mat-error class="font-extra-small error-message-spacing">{{ error }}</mat-error>
                  }
                </mat-form-field>
                @if (isMedicalProcessingClass()) {
                  <mat-icon
                            [matTooltip]="getInfoTooltip('Activity End Date')" class="info-icon">help_outline
                  </mat-icon>
                }
              }

            </mat-grid-tile>
            <!-------------------------------------->
            <!--.Activity Start/End (End) Section-->
            <!-------------------------------------->

            <mat-grid-tile>
              <mat-form-field [ngClass]="{
              'w-30': isActivityWithSpecifiedSchemaType(subActivityGroup, codingSchemaType.NationalPharmaceuticalProductIndexId) && isAuthorisation(),
              'w-45': (
                      (isActivityWithSpecifiedSchemaType(subActivityGroup, codingSchemaType.NationalPharmaceuticalProductIndexId) && isAuthorisation()) &&
                      !subActivityGroup.get(ActivityControls.IsCollapsedRepeatAuthorized).value
                      )
              }">
                <mat-label>Quantity</mat-label>
                <input [formControl]="subActivityGroup.get(ActivityControls.Quantity)" type="number" min="0" matInput required>
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Quantity')" class="info-icon">help_outline</mat-icon>


              @if (isAuthorisation() && isActivityWithSpecifiedSchemaType(subActivityGroup, codingSchemaType.NationalPharmaceuticalProductIndexId)) {

                <mat-slide-toggle class="m-l-20" matInput
                                  [formControl]="subActivityGroup.get(ActivityControls.IsCollapsedRepeatAuthorized)"
                                  (change)="onChangeRepeatAuthorizedToggleChange($event, subActivityGroup)">
                  @if (!subActivityGroup.get(ActivityControls.IsCollapsedRepeatAuthorized).value) {
                    Specify Repeats
                  }
                </mat-slide-toggle>

                @if (subActivityGroup.get(ActivityControls.IsCollapsedRepeatAuthorized).value) {
                  <mat-form-field class="w-30 m-l-5">
                    <mat-label>Repeats</mat-label>
                    <input [formControl]="subActivityGroup.get(ActivityControls.RepeatAuthorised)" type="number" min="0"
                           matInput>
                  </mat-form-field>
                  <mat-icon [matTooltip]="getInfoTooltip('Repeats Authorised')" class="info-icon">help_outline
                  </mat-icon>
                }

              }

            </mat-grid-tile>

            <!-- Row-2 -->
            <mat-grid-tile>
              <mat-form-field>
                <mat-label>Tariff Code Description</mat-label>
                <input required matInput type="text" [placeholder]="getInfoTooltip('Coding Schema Description')"
                       [formControl]="subActivityGroup.get(ActivityControls.CodingSchema)"
                       (blur)="blurCodingSchemaDescription(subActivityGroup.get(ActivityControls.CodingSchema))"
                       (input)="onInputChangedSearchCodingSchemas($event.target.value + ';' + subActivityGroup.get(ActivityControls.CodingSchemaType).value)"
                       [matAutocomplete]="codingSchemaAuto">
                <mat-autocomplete #codingSchemaAuto="matAutocomplete" [panelWidth]="'50vw'">
                  @for (schema of filteredCodingSchemas; track getCodingSchemaDisplayString(schema)) {
                    <mat-option
                      (onSelectionChange)="onCodingSchemaOptionChanged($event.source.value, subActivityGroup.get(ActivityControls.CodingSchema))"
                      [value]="getCodingSchemaDisplayString(schema)"
                      class="custom-mat-option">
                      {{getCodingSchemaDisplayString(schema)}}
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (getSelectedCodingSchema(subActivityGroup.get(ActivityControls.CodingSchema).value)) {
                  <button (click)="clearCodingSchemaDescription(subActivityGroup.get(ActivityControls.CodingSchema))"
                          aria-label="Clear"
                          mat-icon-button matSuffix>
                    <mat-icon style="color: grey;">clear</mat-icon>
                  </button>
                }
                @if (showActivityTuneIndicators(subActivityGroup)) {
                  <button (click)="onClickShowActivitiesOverrideIndicatorsDialog($event, subActivityGroup)"
                          aria-label="Activity Override Indicators"
                          mat-icon-button matSuffix>
                    <mat-icon
                      class="hand-cursor info-icon activity-tune-icon"
                      [matBadge]="getActiveActivityOverrideCount(subActivityGroup)"
                      [matBadgeHidden]="getActiveActivityOverrideCount(subActivityGroup) === 0"
                      matBadgeColor="warn"
                      matBadgeSize="small">tune</mat-icon>
                  </button>
                }
              </mat-form-field>
              <mat-icon [matTooltip]="getInfoTooltip('Coding Schema Description')" class="info-icon">help_outline</mat-icon>
            </mat-grid-tile>


            @if (isProviderPortal) {
              @if (isMedicalClaim()) {
                <mat-grid-tile>
                  <ng-container *ngTemplateOutlet="amountBilled;"></ng-container>
                </mat-grid-tile>
              }
            }

            <!---------------------------->
            <!--Section Visible: Non-Providers Portal Users-->
            <!---------------------------->
            @if (!isProviderPortal) {
              @if (isMedicalProcessingClass()) {
                <mat-grid-tile>
                  <mat-form-field class="w-35">
                    <mat-label>Start Time</mat-label>
                    <input [formControl]="subActivityGroup.get(ActivityControls.ActivityStartTime)" matInput
                           [required]="isMedicalProcessingClass()" type="time"
                           max="{{subActivityGroup.get(ActivityControls.ActivityEndTime).value}}">
                  </mat-form-field>
                  <mat-icon
                    [matTooltip]="getInfoTooltip('Activity Start Time')" class="info-icon">help_outline
                  </mat-icon>
                  <mat-form-field class="w-35 m-l-15">
                    <mat-label>End Time</mat-label>
                    <input [formControl]="subActivityGroup.get(ActivityControls.ActivityEndTime)" matInput
                           [required]="isMedicalProcessingClass()" type="time"
                           min="{{subActivityGroup.get(ActivityControls.ActivityStartTime).value}}">
                  </mat-form-field>
                  <mat-icon
                    [matTooltip]="getInfoTooltip('Activity End Time')" class="info-icon">help_outline
                  </mat-icon>
                </mat-grid-tile>
              }

              <mat-grid-tile>
                <mat-form-field
                  [ngClass]="{'w-40': isMedicalClaim()}">
                  <mat-label>{{amountClaimedLabel}}</mat-label>
                  <input [formControl]="subActivityGroup.get(ActivityControls.AmountClaimed)" mask="separator.2"
                         matInput thousandSeparator="," (blur)="onAmountClaimedValue(i)"
                         [required]="isAmountRequired(subActivityGroup)">
                </mat-form-field>
                <mat-icon [matTooltip]="getInfoTooltip('Amount Claimed')" class="info-icon">help_outline</mat-icon>

                @if (isMedicalClaim()) {
                  <ng-container *ngTemplateOutlet="amountBilled; context: {$implicit: 'w-35'}"></ng-container>
                }
              </mat-grid-tile>

            } @else {
              <mat-grid-tile></mat-grid-tile>
            }

            <!-- Row-3 -->
            @if (isMedicalProcessingClass()) {
              <mat-grid-tile>
                <mat-form-field>
                  <mat-label>Activity Type</mat-label>
                  <mat-select [formControl]="subActivityGroup.get(ActivityControls.ActivityType)" required>
                    @for (type of activityTypes; track type.name) {
                      <mat-option [value]="type.name">
                        {{type.name}}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                @if (isMedicalProcessingClass()) {
                  <mat-icon
                            [matTooltip]="getInfoTooltip('Activity Type')" class="info-icon">help_outline
                  </mat-icon>
                }
              </mat-grid-tile>
            }

            <!---------------------------->
            <!--Section Visible: Non-Providers Portal Users-->
            <!---------------------------->
            @if (isDaysSuppliedVisible(i)) {
              <mat-grid-tile>
                <mat-form-field>
                  <mat-label>Days Supplied</mat-label>
                  <input [formControl]="subActivityGroup.get(ActivityControls.DaysSupplied)"
                         type="number" [min]="0" matInput required>
                </mat-form-field>
                <mat-icon [matTooltip]="getInfoTooltip('Days Supplied')" class="info-icon">help_outline
                </mat-icon>
              </mat-grid-tile>
            }

            @if (isPrimarySubClassPharmacy) {
              <mat-grid-tile>
                <mat-form-field >
                  <mat-label>Diagnoses Association</mat-label>
                  <mat-select [formControl]="subActivityGroup.get(ActivityControls.Diagnoses)" required multiple>
                    @for (diagnosis of getSortedSelectedDiagnoses(); track diagnosis) {
                      <mat-option [value]="diagnosis">
                        {{getActivityDiagnosisDisplayString(diagnosis)}}
                      </mat-option>
                    }
                  </mat-select>
                  @if (subActivityGroup.get(ActivityControls.Diagnoses)?.invalid) {
                    <mat-error>
                      @if (subActivityGroup.get(ActivityControls.Diagnoses).errors?.atleastOneValue) {
                        <span>
                          At least one diagnosis should be assigned to the activity
                        </span>
                      }
                    </mat-error>
                  }
                </mat-form-field>
                <mat-icon [matTooltip]="getInfoTooltip('Diagnoses Association')" class="info-icon">help_outline</mat-icon>
              </mat-grid-tile>
            }

          </mat-grid-list>

          <mat-grid-list cols="1" rowHeight="90px">
            <mat-grid-tile>
              @if (activityFormArray.length > 1) {
                <button mat-raised-button color="warn"
                        class="add-form-group-button m-r-15" (click)="removeActivityFormGroup(subActivityGroup)">
                  <mat-icon>delete</mat-icon>
                   Remove Activity
                </button>
              }
              <button mat-raised-button color="primary"
                      class="add-form-group-button" (click)="openObservationDialog(subActivityGroup)">
                <mat-icon>lists</mat-icon>
                  Observations
              </button>
            </mat-grid-tile>
          </mat-grid-list>


          <ng-template #amountBilled let-classes>
            <mat-form-field [class]="classes">
              <mat-label>Amount Billed</mat-label>
              <input [formControl]="subActivityGroup.get(ActivityControls.AmountBilled)"
                     matInput mask="separator.3" thousandSeparator="," required
                     (blur)="onAmountBilledValue(i)">
            </mat-form-field>
            <mat-icon [matTooltip]="getInfoTooltip('Amount Billed')" class="info-icon">help_outline</mat-icon>
          </ng-template>
        </form>

          <div class="whitespace-content m-l-15" [hidden]="subActivityGroup.get(ActivityControls.IsCollapsed).value">
            @if (subActivityGroup.invalid) {
              <p class="warn-text">
                <mat-icon class="warn-icon">error_outline</mat-icon>
                <span>Missing information</span>
              </p>
            }

            @if (getActivityInformation(subActivityGroup, i); as activityInformation) {
              <div class="activity">
                <div class="header-row">
                  <span class="activity-label">Activity {{ activityInformation.activityNumber }}:</span>
                    <span class="activity-value">{{ activityInformation.activityTariffDescription }}</span>
                </div>

                <div class="detail-row">
                  <span class="spacer"></span><span class="key">Date:</span><span class="value">{{ activityInformation.date }}</span>
                </div>

                <div class="detail-row">
                  <span class="spacer"></span><span class="key">Quantity:</span><span class="value">{{ activityInformation.quantity }}</span>
                </div>

                <div class="detail-row">
                  <span class="spacer"></span><span class="key">Amount:</span><span class="value">{{ activityInformation.amount }}</span>
                </div>

                @if (activityInformation.diagnosis) {
                  <div class="detail-row">
                    <span class="spacer"></span><span class="key">Diagnosis:</span><span class="value">{{ activityInformation.diagnosis }}</span>
                  </div>
                }

                <div class="detail-row">
                  <span class="spacer"></span><a class="key observation-link" (click)="openObservationDialog(subActivityGroup)">Observations:</a>
                  <span class="value observation-text" [matTooltip]="activityInformation.observations || ''">{{ activityInformation.observations || '—' }}</span>
                </div>
              </div>
            }
          </div>
          @if (activityFormArray?.length > 0) {
            <mat-divider></mat-divider>
          }
        </div>
      }

      <!---------------------------->
      <!-- Button Section-->
      <!---------------------------->

      <div class="button-container">
        <button #activityBackButton mat-raised-button matStepperPrevious color="primary">Back</button>
        <button #activityClearButton mat-raised-button color="warn"
                (click)="resetActivityFormGroup()"
                [disabled]="!isAllowedToSubmitTransaction()">
          Clear
          <ng-container [ngTemplateOutlet]="helpIcon"></ng-container>
        </button>
        <button #finishButton mat-raised-button matStepperNext
                color="primary"
                [disabled]="isFinishButtonDisabled()"
                (click)="submitClaim()">
          {{processButtonLabel}}
          @if (isFinishButtonDisabled()) {
            <mat-icon class="info-icon tooltip-button-disabled-wrapper"
                      [matTooltip]="getFinishButtonTooltip()">help_outline
            </mat-icon>
          }
        </button>
        @if (isReview && isAdjudicationStatusUpdated) {
          <div class="process-reminder" role="status">
            <mat-icon>priority_high</mat-icon>
            Reprocess for overrides to take effect
          </div>
        }
      </div>
    </mat-step>


    <ng-template #helpIcon>
      @if (!isAllowedToSubmitTransaction()) {
        <mat-icon class="info-icon"
                  [matTooltip]="getClearButtonTooltip()">
          help_outline
        </mat-icon>
      }
    </ng-template>
  </mat-horizontal-stepper>
</div>
